import TextInput from "@/components/form/TextInput";
import UserRoleDropdown from "@/components/form/UserRoleDropdown";
import { Button } from "@/components/ui/button";
import { RoleType, User } from "@/models/user.model";
import { ChevronLeft } from "lucide-react";
import React, { FormEvent, useEffect } from "react";
import userImage from "@/assets/images/user.png";
import { useNavigate } from "react-router-dom";
import EditButton from "@/components/buttons/EditButton";
import { updateUser } from "@/api/users";
import { toast } from "@/hooks/use-toast";
import { useCurrentUser } from "@/context/UserContext";
import UserAvatar from "@/components/user/UserAvatar";
interface Props {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
}
function BasicInfo({ user, setUser }: Props) {
  const { currentUser } = useCurrentUser();
  const [fullName, setFullName] = React.useState(user?.fullName || "");
  const [email, setEmail] = React.useState(user?.email || "");
  const [role, setRole] = React.useState(RoleType.STAFF);
  const [err, setErr] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [isReadOnly, setIsReadOnly] = React.useState(true);

  useEffect(() => {
    setFullName(user?.fullName || "");
    setEmail(user?.email || "");
    setRole((user?.role as RoleType) || RoleType.STAFF);
  }, [user]);
  const handleCancel = () => {
    setFullName(user?.fullName || "");
    setEmail(user?.email || "");
    setRole((user?.role as RoleType) || RoleType.STAFF);
    setIsReadOnly(true);
  };

  const isValid = () => {
    if (fullName.length === 0 || email.length === 0) {
      setErr("All fields are required.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    setIsSubmitting(true);
    e.preventDefault();
    if (!isValid()) {
      return;
    }

    try {
      const res = await updateUser(
        user?.id as number,
        fullName,
        email
      );
      if (res) {
        setErr("");
        setIsReadOnly(true);
        setUser(res);
        toast({
          title: "User updated!",
          description: `${res.fullName} has been successfully updated`,
        });
      } else {
        toast({
          title: "An error occurred",
          description: "Please try again",
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error(err);
      toast({
        title: "An error occurred",
        description: "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isNoChange = () => {
    return (
      fullName === user?.fullName &&
      email === user?.email &&
      role === user?.role
    );
  };

  return (
    <div className={`h-full flex  flex-col items-start gap-8`}>
      {/* your profile + avatar */}
      <div className="flex flex-col w-full items-center gap-4 flex-1 min-w-[150px] md:min-w-[200px]">
        <div className="text-2xl font-bold self-start pl-2">
          {currentUser?.role === RoleType.ADMIN ? `${user?.fullName}'s Profile` : "Your Profile"}
        </div>

        <div className="min-w-[100px] w-1/4 lg:w-1/2 lg:max-w-[200px] flex items-center justify-center h-full">
          <UserAvatar
            fullName={fullName}
            textSize="text-3xl"
          />
        </div>
      </div>

      <form className="w-full h-full flex flex-col gap-4 mb-4">
        <div className="flex justify-end">
          <EditButton
            handleCancel={handleCancel}
            handleSave={handleSubmit}
            handleEditing={() => setIsReadOnly(false)}
            isEditing={isReadOnly}
            isNoChange={isNoChange}
            isSubmitting={isSubmitting}
          />
        </div>

        <div className="flex gap-2">
          <TextInput
            type="text"
            label="First Name"
            value={fullName}
            setValue={setFullName}
            readOnly={isReadOnly}
          />

        </div>

        <TextInput
          type="email"
          label="Email"
          value={email}
          setValue={setEmail}
          readOnly={isReadOnly}
        />
        <UserRoleDropdown
          selectedRole={role}
          setSelectedRole={setRole}
          isReadOnly={true}
        />

      </form>

    </div>
  );
}

export default BasicInfo;
